import { useField, useFormikContext } from 'formik'
import { noop } from 'lodash'
import { equals } from 'ramda'
import { memo, useState, useRef, forwardRef, FocusEvent } from 'react'
import ReactSelect, {
  components,
  DropdownIndicatorProps,
  ValueContainerProps,
  InputActionMeta,
  ActionMeta,
  StylesConfig,
  MultiValue,
  SelectComponentsConfig,
  GroupBase,
  Options
} from 'react-select'
import AsyncSelect from 'react-select/async'
import AsyncCreatableSelect from 'react-select/async-creatable'
import styled from 'styled-components'
import Icon from '@/components/icon'
import useFocusOnError from '@/hooks/use-focus-on-error'
import { colors, transitions } from '@/styles'
import toId from '@/utils/to-id'
import Label, { active } from './floating-label'
import Error from './error'

const Wrapper = styled.div<{ hasError?: boolean; isFilled?: boolean; fullWidth?: boolean }>`
  ${props =>
    props.hasError &&
    `
    > div {
      div {
        border-color: ${colors.red} !important;
      }
    }
  `}

  position: relative;
  ${props =>
    props.fullWidth &&
    `
      width: 100%;
    `}

  ${props =>
    props.isFilled &&
    `
    > label:last-of-type {
      ${active}
      padding-left: 10px;
    }
    .value-container {
      margin-top: 10px;
      text-align: left;
    }
  `}
`

const IconWrapper = styled.div`
  position: relative;
  > svg {
    position: absolute;
    right: 10px;
    top: -14px;
    transform: rotate(180deg);
    path {
      fill: ${colors.mediumGrey};
    }
  }
`

const ValueContainer: React.FC<ValueContainerProps<OptionPair>> = ({ children, ...props }) => (
  <components.ValueContainer className="value-container" {...props}>
    {children}
  </components.ValueContainer>
)

const DropdownIndicator: React.FC<DropdownIndicatorProps<OptionPair>> = () => (
  <IconWrapper>
    <Icon id="arrow" height={20} width={20} />
  </IconWrapper>
)

type StyleConfig = StylesConfig<OptionPair>

const getStyles = (styles: StyleConfig): StyleConfig => ({
  container: (provided, state) => ({
    ...provided,
    ...styles.container,
    opacity: state.isDisabled ? 0.3 : 1
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected || state.isFocused ? '#e3e9ee' : 'transparent',
    color: colors.regular,
    ...styles.option
  }),
  input: provided => ({
    ...provided,
    margin: '0',
    color: '#000',
    fontSize: 14,
    letterSpacing: -0.2
  }),
  menuPortal: ({ left, ...provided }) => ({
    ...provided,
    ...styles.menuPortal
  }),
  menu: provided => ({
    ...provided,
    marginTop: 0,
    zIndex: 100,
    ...styles.menu
  }),
  control: provided => ({
    ...provided,
    height: 48,
    transition: `border-color ${transitions.ease}`,
    borderRadius: 6,
    border: `1px solid ${colors.grey}`,
    backgroundColor: colors.white,
    boxShadow: 'none',
    '&:hover': {
      borderColor: colors.secondary
    },
    ...styles.control
  }),
  indicatorSeparator: () => ({}),
  indicatorsContainer: () => ({
    ...styles.indicatorsContainer
  }),
  placeholder: provided => ({
    ...provided,
    ...styles.placeholder,
    marginLeft: 0,
    color: '#ccc'
  }),
  singleValue: provided => ({
    ...provided,
    marginLeft: 0,
    marginRight: 0,
    color: '#000',
    fontSize: 14,
    letterSpacing: -0.2,
    ...styles.singleValue
  }),
  valueContainer: provided => ({
    ...provided,
    padding: '2px 12px'
  })
})

const getAsyncComponent = (creatable?: boolean) => (creatable ? AsyncCreatableSelect : AsyncSelect)

type OptionPair = {
  value: string
  label: string
}

type Event = FocusEvent<HTMLInputElement>

type DropdownProps = {
  styles?: StyleConfig
  creatable?: boolean
  loadOptions?: (
    value: string,
    callback: (o: Options<OptionPair>) => void
  ) => Promise<Options<OptionPair>>
  value?: OptionPair | null
  placeholder?: string
  components?: SelectComponentsConfig<OptionPair, boolean, GroupBase<OptionPair>>
  id?: string
  onChange?: (
    value: OptionPair | MultiValue<OptionPair> | null,
    action: ActionMeta<OptionPair>
  ) => void
  onInputChange?: (value: string, action: InputActionMeta) => void
  disabled?: boolean
  isSearchable?: boolean
  hasError?: boolean
  defaultValue?: OptionPair
  children?: React.ReactNode
  options: Options<OptionPair>
  onBlur?: (e: Event) => void
  autoComplete?: string
  fullWidth?: boolean
}

const DropdownRaw = forwardRef<HTMLDivElement, DropdownProps>((props, ref) => {
  const {
    styles = {},
    creatable,
    value,
    placeholder,
    components,
    id: initialId,
    onChange = noop,
    onInputChange = noop,
    disabled,
    isSearchable = false,
    hasError,
    defaultValue,
    children,
    options = [],
    fullWidth = false,
    ...rest
  } = props
  const [isFilled, setIsFilled] = useState(!!defaultValue)
  const Component = rest.loadOptions ? getAsyncComponent(creatable) : ReactSelect
  const onInputChangeWrap: DropdownProps['onInputChange'] = (val, options) => {
    const { action } = options
    setIsFilled(filled => (action === 'input-change' || action === 'menu-close' ? !!val : filled))
    return onInputChange(val, options)
  }
  const onChangeWrap: DropdownProps['onChange'] = (val, options) => {
    const { action } = options
    setIsFilled(action !== 'clear')
    return onChange(val, options)
  }
  const id = initialId || toId(placeholder) || undefined

  return (
    <Wrapper ref={ref} hasError={hasError} isFilled={isFilled || !!value} fullWidth={fullWidth}>
      <Component
        components={{ DropdownIndicator, ValueContainer, ...components }}
        options={options}
        {...rest}
        onChange={onChangeWrap}
        onInputChange={onInputChangeWrap}
        defaultValue={defaultValue}
        placeholder=""
        inputId={id}
        value={value}
        styles={getStyles(styles)}
        isDisabled={disabled}
        isSearchable={isSearchable}
        onFocus={e => {
          // Prevents chrome autofill
          if (e.target.autocomplete) {
            /* eslint-disable no-param-reassign */
            e.target.autocomplete = 'new-password'
            /* eslint-enable no-param-reassign */
          }
        }}
        // Disable blur when selection is made. This was triggering validation issues
        // on mobile browsers due to: https://github.com/jaredpalmer/formik/issues/2457
        blurInputOnSelect={false}
      />
      {placeholder && (
        <Label x={2} htmlFor={id}>
          {children} {placeholder}
        </Label>
      )}
    </Wrapper>
  )
})

const Dropdown = memo(DropdownRaw)

Dropdown.displayName = 'Dropdown'

export default Dropdown

const findValue = (options: Options<OptionPair> = [], value: string): OptionPair | null =>
  options.find(option => equals(option?.value, value)) || null

type Key = string | string[] | null

type FieldDropdownProps = Omit<DropdownProps, 'onChange' | 'disabled'> & {
  name: string
  onChange?: (v: Key) => void
  isDisabled?: boolean
  isSearchable?: boolean
}

const FieldDropdownRaw: React.FC<FieldDropdownProps> = ({
  name,
  onChange = noop,
  options = [],
  isDisabled,
  isSearchable = false,
  ...rest
}) => {
  const formik = useFormikContext()
  const [field, meta] = useField(name)
  const fieldRef = useRef()
  useFocusOnError({ fieldRef, name })
  const { onBlur, ...fields } = field
  const hasError = !!meta.error && meta.touched
  const onBlurWrapper = (e: Event) => {
    const touched: any = { ...formik.touched }
    touched[field.name] = true
    formik.setTouched(touched)
    onBlur(e)
  }
  const onChangeWrap: DropdownProps['onChange'] = option => {
    const value: Key = (() => {
      if (!option) return null
      if (Array.isArray(option)) return option.map(el => el.value)
      return (option as OptionPair).value
    })()
    onChange(value)
    formik.setFieldValue(field.name, value)
  }
  return (
    <>
      <Dropdown
        onBlur={onBlurWrapper}
        {...fields}
        hasError={hasError}
        options={options}
        value={findValue(options, field.value)}
        onChange={onChangeWrap}
        disabled={isDisabled}
        isSearchable={isSearchable}
        {...rest}
      />
      <Error name={name} />
    </>
  )
}

export const FieldDropdown = memo(FieldDropdownRaw)

FieldDropdown.displayName = 'FieldDropdown'
